import React, { useEffect, useRef, useState } from 'react';
import { Upload, Camera, X, ZoomIn, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import * as microsoftTeams from '@microsoft/teams-js';
import { isInTeams } from '@/integrations/auth/entra';
import { toast } from '@/components/ui/use-toast';
import { compressReceiptImageIfNeeded } from '@/lib/imageCompression';

interface ReceiptFilePickerProps {
  /** The currently selected file, or null. */
  file: File | null;
  /** Called with the new file, or null when cleared. */
  onSelect: (file: File | null) => void;
  /** Text shown in the empty drop zone. */
  emptyLabel?: string;
}

/**
 * Pick a receipt and show it. Once a file is chosen we render a preview (an image
 * thumbnail) plus buttons to choose a different file or remove it. Clicking the
 * thumbnail opens a full-size view so the receipt can be read before saving. The object
 * URL used for the preview is revoked on change/unmount so there is no memory leak.
 */
export function ReceiptFilePicker({ file, onSelect, emptyLabel = 'Choose a file from your device' }: ReceiptFilePickerProps) {
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [compressing, setCompressing] = useState(false);

  // Only mobile devices can usefully "take a photo" (the capture input opens the OS
  // camera). On desktop capture is ignored, so we hide the camera button there entirely.
  // Coarse pointer = touchscreen; UA check covers phones/tablets incl. the Teams webview.
  const [isMobile] = useState(
    () =>
      typeof navigator !== 'undefined' &&
      (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
        (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true)),
  );

  // Inside a Teams tab the <input capture> trick is NOT honored by the webview (it silently
  // falls back to a file picker), so there we must use the Teams media API instead. Detect
  // it once on mount.
  const [inTeams, setInTeams] = useState(false);
  useEffect(() => {
    isInTeams().then(setInTeams).catch(() => setInTeams(false));
  }, []);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // "Choose a file from your device": in Teams open the device PHOTO LIBRARY (gallery source) —
  // a plain file input inside the Teams webview shows Teams/OneDrive files, not the device. In a
  // normal browser the hidden file input already reaches the device gallery/files.
  const openGallery = () => {
    if (inTeams) {
      selectFromDeviceGallery();
    } else {
      galleryInputRef.current?.click();
    }
  };

  // Camera button: in Teams open the camera DIRECTLY (captureImage = camera only, no chooser).
  // In a normal browser <input capture="environment"> opens the OS camera.
  const openCamera = () => {
    if (inTeams) {
      captureFromCamera();
    } else {
      cameraInputRef.current?.click();
    }
  };

  // Ensure camera + photo permission in Teams. Beta API — if the host doesn't support it,
  // ignore and let the media call trigger Teams' own consent prompt.
  const ensureMediaPermission = async () => {
    try {
      if (!(await microsoftTeams.media.hasPermission())) {
        await microsoftTeams.media.requestPermission();
      }
    } catch {
      /* permission API not supported on this host */
    }
  };

  const fileFromBlob = (blob: Blob, mimeType: string): File => {
    const mime = mimeType || blob.type || 'image/jpeg';
    const ext = (mime.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
    return new File([blob], `receipt.${ext}`, { type: mime });
  };

  // Teams' base64 `preview` (and any data: URL) → a Blob, so the preview thumbnail can be
  // used as a fallback when getMedia fails to stream the full-resolution bytes.
  const base64ToBlob = (base64: string, mime: string): Blob => {
    const raw = base64.includes(',') ? base64.split(',')[1] : base64;
    const binary = atob(raw);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime || 'image/jpeg' });
  };

  // Hands the picked file to onSelect, downscaling it first when it came from the gallery
  // (an existing photo, possibly a huge one straight off the phone's camera roll) rather than
  // our own camera button (which produces a normal-sized shot, so it's left untouched).
  const finalizeSelection = async (picked: File, isGallery: boolean) => {
    if (!isGallery) {
      onSelect(picked);
      return;
    }
    setCompressing(true);
    try {
      onSelect(await compressReceiptImageIfNeeded(picked));
    } finally {
      setCompressing(false);
    }
  };

  // One Teams media flow for both buttons — only the source differs. selectMedia returns
  // metadata, then getMedia() streams the actual bytes back as a Blob (the same proven path
  // for camera and gallery; captureImage's base64 was unreliable). A single Camera/Gallery
  // source opens that source directly with no chooser.
  const pickViaTeams = async (
    source: number,
    errorTitle: string,
    errorDescription: string,
  ) => {
    const isGallerySource = source === microsoftTeams.media.Source.Gallery;
    try {
      await ensureMediaPermission();
      microsoftTeams.media.selectMedia(
        {
          mediaType: microsoftTeams.media.MediaType.Image,
          maxMediaCount: 1,
          imageProps: { sources: [source] },
        },
        (error, attachments) => {
          if (error) {
            // USER_ABORT = the user closed the camera/picker without choosing — not an error.
            if (error.errorCode !== microsoftTeams.ErrorCode.USER_ABORT) {
              toast({ variant: 'destructive', title: errorTitle, description: errorDescription });
            }
            return;
          }
          const att = attachments?.[0];
          if (!att) return;
          const mime = att.mimeType || 'image/jpeg';

          // getMedia streams the full-res bytes but is unreliable in the Teams mobile webview:
          // it can return an empty blob or never call back at all. So we (a) time out after a
          // few seconds, and (b) fall back to the base64 `preview` thumbnail — a lower-res image
          // beats no receipt. `settled` guards against a late getMedia callback double-firing.
          let settled = false;
          const usePreviewFallback = () => {
            if (settled) return;
            if (att.preview) {
              try {
                settled = true;
                finalizeSelection(fileFromBlob(base64ToBlob(att.preview, mime), mime), isGallerySource);
                toast({ title: 'Receipt added in lower resolution',
                  description: 'The full-size image was unavailable, so a compressed version was used. '
                    + 'Pick it from your gallery instead if you need full quality.' });
                return;
              } catch {
                /* preview unusable — fall through to the error toast below */
              }
            }
            settled = true;
            toast({ variant: 'destructive', title: 'Could not read the image', description: 'Please try again.' });
          };

          const timer = setTimeout(usePreviewFallback, 8000);
          att.getMedia((mediaError, blob) => {
            clearTimeout(timer);
            if (settled) return;
            if (mediaError || !blob || blob.size === 0) {
              usePreviewFallback();
              return;
            }
            settled = true;
            finalizeSelection(fileFromBlob(blob, mime), isGallerySource);
          });
        },
      );
    } catch {
      toast({ variant: 'destructive', title: errorTitle, description: errorDescription });
    }
  };

  // Teams: open the camera directly (single Camera source = no chooser).
  const captureFromCamera = () =>
    pickViaTeams(
      microsoftTeams.media.Source.Camera,
      'Could not open the camera',
      'Check that Teams has camera permission on your device.',
    );

  // Teams: pick from the device photo library (single Gallery source).
  const selectFromDeviceGallery = () =>
    pickViaTeams(
      microsoftTeams.media.Source.Gallery,
      'Could not open your photos',
      'Check that Teams has photo permission on your device.',
    );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, isGallery: boolean) => {
    const picked = e.target.files?.[0];
    // Reset so picking the very same file again still fires onChange.
    e.target.value = '';
    if (picked) finalizeSelection(picked, isGallery);
  };

  const isImage = file?.type.startsWith('image/');

  return (
    <div className="space-y-2">
      {/* Two inputs so the user explicitly picks gallery vs. camera. On mobile the capture
          input opens the camera directly; the plain one lets them pick an existing photo/file. */}
      <input ref={galleryInputRef} type="file" accept="image/*" onChange={(e) => handleChange(e, true)} className="hidden" />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={(e) => handleChange(e, false)} className="hidden" />

      {compressing && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Shrinking image…
        </p>
      )}

      {file ? (
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 rounded-lg border overflow-hidden">
            {isImage && previewUrl ? (
              <button
                type="button"
                onClick={() => setZoomOpen(true)}
                className="group relative block w-full cursor-zoom-in"
                title="Click to view full size"
              >
                <img
                  src={previewUrl}
                  alt={file.name}
                  className="max-h-56 w-full object-contain bg-muted/40"
                />
                <span className="absolute right-2 top-2 rounded-full bg-background/80 p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <ZoomIn className="h-4 w-4" />
                </span>
              </button>
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground bg-muted/40">
                Preview not available for this file type
              </div>
            )}
            <p className="px-2 py-1 text-xs text-muted-foreground truncate" title={file.name}>
              {file.name}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Button type="button" variant="outline" size="sm" onClick={openGallery} disabled={compressing}>
              Choose new
            </Button>
            {isMobile && (
              <Button type="button" variant="outline" size="sm" onClick={openCamera} disabled={compressing}>
                <Camera className="h-4 w-4 mr-1" />
                Camera
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => onSelect(null)} disabled={compressing}>
              <X className="h-4 w-4 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          {/* Left: camera — opens the device camera directly on mobile (capture input).
              Hidden on desktop, where capture is ignored and it would just duplicate upload. */}
          {isMobile && (
            <Button
              type="button"
              variant="outline"
              onClick={openCamera}
              title="Take a photo with the camera"
              className="h-auto flex-col gap-1 px-4 py-4"
              disabled={compressing}
            >
              <Camera className="h-6 w-6 text-muted-foreground" />
              <span className="text-xs font-normal text-muted-foreground">Camera</span>
            </Button>
          )}
          {/* Right: pick an existing file from the device. */}
          <button
            type="button"
            onClick={openGallery}
            disabled={compressing}
            className="flex-1 border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{emptyLabel}</p>
          </button>
        </div>
      )}

      {isImage && previewUrl && (
        <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] p-0">
            <DialogHeader className="p-6 pb-4 pr-14">
              <DialogTitle className="truncate" title={file?.name}>
                {file?.name ?? 'Receipt'}
              </DialogTitle>
            </DialogHeader>
            <div className="flex justify-center px-6 pb-6">
              <img
                src={previewUrl}
                alt={file?.name ?? 'Receipt'}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
